import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authedFileHref } from '../utils/files';
import { formatDateKey } from '../utils/dates';
import ComponentTypeSettings from '../components/ComponentTypeSettings';

// Equipment registry (E1, generalised from vehicles): vans, toolboxes and
// hired kit share one registry, typed by the configurable equipment_types
// list. Vans keep the vehicle-specific columns (registration, MOT, seats,
// odometer); other types identify by serial/reference.

const emptyEquipmentForm = {
  equipment_type: 'van',
  registration_number: '',
  serial_number: '',
  number_of_seats: '',
  make: '',
  model: '',
  last_mot_date: '',
  service_interval_miles: '',
  current_odometer_reading: '',
  active: true,
  notes: '',
  inflow_warehouse_code: '',
  is_hired: false,
  hire_reference: '',
  hire_start_date: '',
  hire_end_date: '',
  assigned_to_user_id: ''
};

// MOT and hire dates are DATE columns: calendar days in every zone.
const formatDate = (value) => formatDateKey(value);

const toInputDate = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const calculateNextMotDueDate = (lastMotDate) => {
  if (!lastMotDate) return '';
  const date = new Date(`${lastMotDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  date.setUTCFullYear(date.getUTCFullYear() + 1);
  return date.toISOString().slice(0, 10);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatYesNo = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const getCheckDataValue = (checkData = {}, field, fallback = '-') => {
  const value = checkData[field];
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : fallback;
  if (typeof value === 'boolean') return formatYesNo(value);
  if (value === null || value === undefined || value === '') return fallback;
  return String(value);
};

const hasAttachments = (check) => Array.isArray(check.attachments) && check.attachments.length > 0;

const equipmentDisplayName = (item) =>
  item?.registration_number || item?.serial_number || '(unnamed)';

function EquipmentCheckDetailModal({ check, onClose }) {
  const checkData = check.check_data || {};

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal vehicle-check-detail-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Equipment Check Details</h2>
            <p className="modal-subtitle">{check.registration_number || check.serial_number} - {formatDateTime(check.submitted_at)}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="check-detail-section">
            <h3>Submission Info</h3>
            <div className="check-detail-grid">
              <div><label>Equipment</label><span>{check.registration_number || check.serial_number || '-'}</span></div>
              <div><label>Submitted by</label><span>{check.submitted_by_name || check.submitted_by_email || '-'}</span></div>
              <div><label>Date/Time</label><span>{formatDateTime(check.submitted_at)}</span></div>
              <div><label>Odometer</label><span>{check.odometer_reading != null ? Number(check.odometer_reading).toLocaleString() + ' mi' : '-'}</span></div>
            </div>
          </div>

          <div className="check-detail-section">
            <h3>Employees Travelling</h3>
            <p>{getCheckDataValue(checkData, 'employees_present_names')}</p>
          </div>

          <div className="check-detail-section">
            <h3>Vehicle Checks</h3>
            <div className="check-detail-grid">
              <div>
                <label>Warning lights/messages</label>
                <span className={checkData.warning_lights_messages ? 'value-warning' : ''}>
                  {formatYesNo(checkData.warning_lights_messages)}
                </span>
              </div>
              <div><label>Warning notes</label><span>{checkData.warning_notes || '-'}</span></div>
              <div><label>AdBlue required</label><span>{formatYesNo(checkData.adblue_required)}</span></div>
              <div><label>Oil sufficient</label><span>{formatYesNo(checkData.oil_sufficient)}</span></div>
              <div><label>Screen wash sufficient</label><span>{formatYesNo(checkData.screen_wash_sufficient)}</span></div>
              <div><label>Lights working</label><span>{formatYesNo(checkData.lights_working)}</span></div>
              <div><label>Tyre pressure correct</label><span>{formatYesNo(checkData.tyre_pressure_correct)}</span></div>
              <div><label>Tyre tread sufficient</label><span>{formatYesNo(checkData.tyre_tread_sufficient)}</span></div>
            </div>
          </div>

          <div className="check-detail-section">
            <h3>Damage</h3>
            <div className="check-detail-grid">
              <div>
                <label>Damage reported</label>
                <span className={checkData.damage ? 'value-danger' : ''}>
                  {formatYesNo(checkData.damage)}
                </span>
              </div>
              <div><label>Description</label><span>{checkData.damage_description || '-'}</span></div>
            </div>
          </div>

          {checkData.additional_information && (
            <div className="check-detail-section">
              <h3>Additional Information</h3>
              <p>{checkData.additional_information}</p>
            </div>
          )}

          {hasAttachments(check) && (
            <div className="check-detail-section">
              <h3>Attachments</h3>
              <div className="check-attachments-list">
                {check.attachments.map((attachment, index) => {
                  const href = authedFileHref(attachment.url);
                  return href ? (
                    <a key={attachment.attachment_id || index} href={href} target="_blank" rel="noreferrer" className="attachment-link">
                      {attachment.name || `Attachment ${index + 1}`}
                    </a>
                  ) : (
                    <span key={attachment.attachment_id || index} className="attachment-link">
                      {attachment.name || `Attachment ${index + 1}`}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {(check.ticket_id || check.work_log_id) && (
            <div className="check-detail-section">
              <h3>Linked Work</h3>
              <div className="check-detail-grid">
                {check.ticket_id && (
                  <div>
                    <label>Ticket</label>
                    <span><Link to={`/tickets/${check.ticket_id}`}>{check.ticket_ref || 'View Ticket'}</Link></span>
                  </div>
                )}
                {check.work_log_id && (
                  <div>
                    <label>Work Log</label>
                    <span>{String(check.work_log_id).slice(0, 8)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function EquipmentChecksTable({ authFetch, equipment, engineers }) {
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [selectedCheck, setSelectedCheck] = useState(null);

  const [filters, setFilters] = useState({
    equipment_id: '',
    submitted_by: '',
    date_from: '',
    date_to: '',
    has_damage: '',
    has_warnings: ''
  });

  const fetchChecks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.equipment_id) params.append('equipment_id', filters.equipment_id);
      if (filters.submitted_by) params.append('submitted_by', filters.submitted_by);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.has_damage) params.append('has_damage', filters.has_damage);
      if (filters.has_warnings) params.append('has_warnings', filters.has_warnings);
      params.append('limit', '100');

      const response = await authFetch(`/api/v1/admin/equipment-checks?${params.toString()}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load equipment checks');
      setChecks(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, filters]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks]);

  const updateFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      equipment_id: '',
      submitted_by: '',
      date_from: '',
      date_to: '',
      has_damage: '',
      has_warnings: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="vehicle-checks-section">
      <div className="checks-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Equipment</label>
            <select value={filters.equipment_id} onChange={e => updateFilter('equipment_id', e.target.value)}>
              <option value="">All equipment</option>
              {equipment.map(item => (
                <option key={item.equipment_id} value={item.equipment_id}>{equipmentDisplayName(item)}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Submitted by</label>
            <select value={filters.submitted_by} onChange={e => updateFilter('submitted_by', e.target.value)}>
              <option value="">Anyone</option>
              {engineers.map(e => (
                <option key={e.user_id} value={e.user_id}>{e.full_name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>From</label>
            <input type="date" value={filters.date_from} onChange={e => updateFilter('date_from', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input type="date" value={filters.date_to} onChange={e => updateFilter('date_to', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Damage</label>
            <select value={filters.has_damage} onChange={e => updateFilter('has_damage', e.target.value)}>
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Warnings</label>
            <select value={filters.has_warnings} onChange={e => updateFilter('has_warnings', e.target.value)}>
              <option value="">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          {hasActiveFilters && (
            <button type="button" className="btn-small btn-secondary" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {error && <div className="admin-message error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading equipment checks...</div>
      ) : checks.length === 0 ? (
        <div className="vehicle-empty">
          {hasActiveFilters ? 'No equipment checks match the current filters.' : 'No equipment checks have been recorded yet.'}
        </div>
      ) : (
        <>
          <div className="checks-summary">
            Showing {checks.length} of {total} checks
          </div>
          <div className="checks-table">
            <table>
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Equipment</th>
                  <th>Submitted by</th>
                  <th>Odometer</th>
                  <th>Employees</th>
                  <th>Warnings</th>
                  <th>Damage</th>
                  <th>Ticket</th>
                  <th>Attachments</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {checks.map(check => {
                  const checkData = check.check_data || {};
                  const employeeCount = Array.isArray(checkData.employees_present_names) ? checkData.employees_present_names.length : 0;
                  const hasWarning = checkData.warning_lights_messages === true;
                  const hasDamage = checkData.damage === true;
                  const attachmentCount = Array.isArray(check.attachments) ? check.attachments.length : 0;

                  return (
                    <tr key={check.check_id}>
                      <td className="col-datetime">{formatDateTime(check.submitted_at)}</td>
                      <td className="col-vehicle">
                        <strong>{check.registration_number || check.serial_number || '-'}</strong>
                      </td>
                      <td className="col-user">{check.submitted_by_name || check.submitted_by_email || '-'}</td>
                      <td className="col-odometer">
                        {check.odometer_reading != null ? Number(check.odometer_reading).toLocaleString() : '-'}
                      </td>
                      <td className="col-employees">
                        {employeeCount > 0 ? `${employeeCount} ${employeeCount === 1 ? 'person' : 'people'}` : '-'}
                      </td>
                      <td className="col-status">
                        <span className={`status-badge ${hasWarning ? 'warning' : 'ok'}`}>
                          {hasWarning ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="col-status">
                        <span className={`status-badge ${hasDamage ? 'danger' : 'ok'}`}>
                          {hasDamage ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="col-ticket">
                        {check.ticket_id ? (
                          <Link to={`/tickets/${check.ticket_id}`}>{check.ticket_ref || 'View'}</Link>
                        ) : '-'}
                      </td>
                      <td className="col-attachments">
                        {attachmentCount > 0 ? (
                          <span className="attachment-count">{attachmentCount}</span>
                        ) : '-'}
                      </td>
                      <td className="col-actions">
                        <button
                          type="button"
                          className="btn-small btn-secondary"
                          onClick={() => setSelectedCheck(check)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {selectedCheck && (
        <EquipmentCheckDetailModal check={selectedCheck} onClose={() => setSelectedCheck(null)} />
      )}
    </div>
  );
}

function EquipmentSettings() {
  const { authFetch, user } = useAuth();
  const [activeTab, setActiveTab] = useState('equipment');
  const [equipment, setEquipment] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [equipmentForm, setEquipmentForm] = useState(emptyEquipmentForm);
  const [typeFilter, setTypeFilter] = useState('');

  // Mirrors the API's authorize(ROLES.OPS) on equipment writes (Gareth
  // 2026-08-11: managers and team leaders run this tab, not just read it).
  const canEdit = ['admin', 'manager', 'team_leader'].includes(user?.role);

  const typeLabel = useCallback((value) => {
    const found = equipmentTypes.find(t => t.value === value);
    return found?.label || value;
  }, [equipmentTypes]);

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/v1/admin/equipment');
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load equipment');
      setEquipment(data.data || []);
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const fetchEquipmentTypes = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/check-form-templates/equipment-types');
      const data = await response.json();
      if (response.ok) setEquipmentTypes(data.data || []);
    } catch (err) {
      console.error('Failed to load equipment types:', err);
    }
  }, [authFetch]);

  const fetchEngineers = useCallback(async () => {
    try {
      const response = await authFetch('/api/v1/tickets/active-engineers');
      const data = await response.json();
      if (response.ok) setEngineers(data.data || []);
    } catch (err) {
      console.error('Failed to load engineers:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchEquipment();
    fetchEquipmentTypes();
    fetchEngineers();
  }, [fetchEquipment, fetchEquipmentTypes, fetchEngineers]);

  const openAddModal = () => {
    setEditingItem(null);
    setEquipmentForm(emptyEquipmentForm);
    setMessage({ text: '', type: '' });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setEquipmentForm({
      equipment_type: item.equipment_type || 'van',
      registration_number: item.registration_number || '',
      serial_number: item.serial_number || '',
      number_of_seats: item.number_of_seats || '',
      make: item.make || '',
      model: item.model || '',
      last_mot_date: toInputDate(item.last_mot_date),
      service_interval_miles: item.service_interval_miles || '',
      current_odometer_reading: item.current_odometer_reading ?? '',
      active: item.active !== false,
      notes: item.notes || '',
      inflow_warehouse_code: item.inflow_warehouse_code || '',
      is_hired: item.is_hired === true,
      hire_reference: item.hire_reference || '',
      hire_start_date: toInputDate(item.hire_start_date),
      hire_end_date: toInputDate(item.hire_end_date),
      assigned_to_user_id: item.assigned_to_user_id || ''
    });
    setMessage({ text: '', type: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setEquipmentForm(emptyEquipmentForm);
  };

  const updateForm = (field, value) => {
    setEquipmentForm(prev => ({ ...prev, [field]: value }));
  };

  const isVanForm = equipmentForm.equipment_type === 'van';

  const buildPayload = () => ({
    equipment_type: equipmentForm.equipment_type,
    registration_number: equipmentForm.registration_number.trim() || null,
    serial_number: equipmentForm.serial_number.trim() || null,
    number_of_seats: equipmentForm.number_of_seats === '' ? null : Number(equipmentForm.number_of_seats),
    make: equipmentForm.make.trim() || null,
    model: equipmentForm.model.trim() || null,
    last_mot_date: equipmentForm.last_mot_date || null,
    service_interval_miles: equipmentForm.service_interval_miles === '' ? null : Number(equipmentForm.service_interval_miles),
    current_odometer_reading: equipmentForm.current_odometer_reading === '' ? null : Number(equipmentForm.current_odometer_reading),
    active: Boolean(equipmentForm.active),
    notes: equipmentForm.notes.trim() || null,
    inflow_warehouse_code: equipmentForm.inflow_warehouse_code.trim() || null,
    is_hired: Boolean(equipmentForm.is_hired),
    hire_reference: equipmentForm.hire_reference.trim() || null,
    hire_start_date: equipmentForm.hire_start_date || null,
    hire_end_date: equipmentForm.hire_end_date || null,
    assigned_to_user_id: equipmentForm.assigned_to_user_id || null
  });

  const saveEquipment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const url = editingItem
        ? `/api/v1/admin/equipment/${editingItem.equipment_id}`
        : '/api/v1/admin/equipment';
      const method = editingItem ? 'PUT' : 'POST';
      const response = await authFetch(url, {
        method,
        body: JSON.stringify(buildPayload())
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save equipment');

      setMessage({ text: editingItem ? 'Equipment updated' : 'Equipment added', type: 'success' });
      closeModal();
      fetchEquipment();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const setEquipmentActive = async (item, active) => {
    if (!canEdit) return;
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await authFetch(`/api/v1/admin/equipment/${item.equipment_id}`, {
        method: 'PUT',
        body: JSON.stringify({ active })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update equipment');
      setMessage({ text: `${equipmentDisplayName(item)} marked ${active ? 'active' : 'inactive'}`, type: 'success' });
      fetchEquipment();
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading equipment...</div>;
  }

  const visibleEquipment = typeFilter
    ? equipment.filter(item => item.equipment_type === typeFilter)
    : equipment;

  return (
    <div className="admin-section vehicle-settings-section">
      <div className="section-header">
        <div>
          <h2>Equipment Admin</h2>
          <p className="section-desc">Manage vans, toolboxes and hired kit, and review their checks.</p>
        </div>
      </div>

      <div className="vehicle-tabs">
        <button
          type="button"
          className={`vehicle-tab ${activeTab === 'equipment' ? 'active' : ''}`}
          onClick={() => setActiveTab('equipment')}
        >
          Equipment
        </button>
        <button
          type="button"
          className={`vehicle-tab ${activeTab === 'checks' ? 'active' : ''}`}
          onClick={() => setActiveTab('checks')}
        >
          Equipment Checks
        </button>
      </div>

      {message.text && (
        <div className={`admin-message ${message.type || 'info'}`}>{message.text}</div>
      )}

      {activeTab === 'equipment' && (
        <>
          <div className="vehicles-header">
            <p className="section-hint">Only active vans appear in work log dropdowns. Equipment types are configured on the Check Forms tab.</p>
            <div className="vehicle-actions">
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {equipmentTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              {canEdit && (
                <button type="button" className="btn-primary" onClick={openAddModal}>
                  + Add Equipment
                </button>
              )}
            </div>
          </div>

          <div className="vehicle-table">
            <div className="vehicle-row header">
              <span>Identifier</span>
              <span>Type</span>
              <span>MOT</span>
              <span>Service / Odometer</span>
              <span>Status</span>
              <span></span>
            </div>
            {visibleEquipment.length === 0 ? (
              <div className="vehicle-empty">No equipment has been added yet.</div>
            ) : visibleEquipment.map(item => (
              <div key={item.equipment_id} className={`vehicle-row ${item.active ? '' : 'inactive'}`}>
                <span className="vehicle-registration">
                  <strong>{equipmentDisplayName(item)}</strong>
                  {(item.make || item.model) && (
                    <small className="vehicle-make-model">{[item.make, item.model].filter(Boolean).join(' ')}</small>
                  )}
                  {item.inflow_warehouse_code && (
                    <small className="vehicle-warehouse">Inflow: {item.inflow_warehouse_code}</small>
                  )}
                  {item.assigned_to_name && <small>Assigned: {item.assigned_to_name}</small>}
                  {item.notes && <small>{item.notes}</small>}
                </span>
                <span>
                  {typeLabel(item.equipment_type)}
                  {item.is_hired && (
                    <>
                      {' '}
                      <span className="chip chip-amber">Hired</span>
                    </>
                  )}
                </span>
                <span className="vehicle-date-stack">
                  <span>Last: {formatDate(item.last_mot_date)}</span>
                  <span>Due: {formatDate(item.next_mot_due_date)}</span>
                </span>
                <span className="vehicle-date-stack">
                  {item.is_hired ? (
                    <>
                      <span>Hire: {formatDate(item.hire_start_date)} - {formatDate(item.hire_end_date)}</span>
                      <span>{item.hire_reference ? `Ref: ${item.hire_reference}` : ''}</span>
                    </>
                  ) : (
                    <>
                      <span>Interval: {item.service_interval_miles ? `${Number(item.service_interval_miles).toLocaleString()} mi` : '-'}</span>
                      <span>Odometer: {item.current_odometer_reading !== null && item.current_odometer_reading !== undefined ? Number(item.current_odometer_reading).toLocaleString() : '-'}</span>
                    </>
                  )}
                </span>
                <span>
                  <span className={`chip ${item.active ? 'chip-green' : 'chip-gray'}`}>
                    {item.active ? 'Active' : 'Inactive'}
                  </span>
                </span>
                <span className="vehicle-actions">
                  {canEdit && (
                    <>
                      <button type="button" className="btn-small btn-secondary" onClick={() => openEditModal(item)}>
                        Edit
                      </button>
                      <button
                        type="button"
                        className={`btn-small ${item.active ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => setEquipmentActive(item, !item.active)}
                        disabled={saving}
                      >
                        {item.active ? 'Mark inactive' : 'Mark active'}
                      </button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {activeTab === 'checks' && (
        <EquipmentChecksTable authFetch={authFetch} equipment={equipment} engineers={engineers} />
      )}

      {showModal && (
        <div className="modal-backdrop" onClick={closeModal}>
          <div className="modal vehicle-modal" onClick={event => event.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Equipment' : 'Add Equipment'}</h2>
              <button type="button" className="modal-close" onClick={closeModal}>
                &times;
              </button>
            </div>

            <form onSubmit={saveEquipment} className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select
                    value={equipmentForm.equipment_type}
                    onChange={event => updateForm('equipment_type', event.target.value)}
                    disabled={Boolean(editingItem)}
                  >
                    {equipmentTypes.filter(t => t.active !== false).map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assigned Engineer</label>
                  <select
                    value={equipmentForm.assigned_to_user_id}
                    onChange={event => updateForm('assigned_to_user_id', event.target.value)}
                  >
                    <option value="">Unassigned / pooled</option>
                    {engineers.map(e => (
                      <option key={e.user_id} value={e.user_id}>{e.full_name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                {isVanForm ? (
                  <div className="form-group">
                    <label>Registration Number *</label>
                    <input
                      value={equipmentForm.registration_number}
                      onChange={event => updateForm('registration_number', event.target.value)}
                      placeholder="AB12 CDE"
                      required
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Serial / Reference *</label>
                    <input
                      value={equipmentForm.serial_number}
                      onChange={event => updateForm('serial_number', event.target.value)}
                      placeholder="e.g. TB-014 or hire plant number"
                      maxLength={100}
                      required
                    />
                  </div>
                )}
                {isVanForm && (
                  <div className="form-group">
                    <label>Number of Seats *</label>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={equipmentForm.number_of_seats}
                      onChange={event => updateForm('number_of_seats', event.target.value)}
                      required
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Make</label>
                  <input
                    value={equipmentForm.make}
                    onChange={event => updateForm('make', event.target.value)}
                    placeholder={isVanForm ? 'Ford' : 'Manufacturer'}
                  />
                </div>
                <div className="form-group">
                  <label>Model</label>
                  <input
                    value={equipmentForm.model}
                    onChange={event => updateForm('model', event.target.value)}
                    placeholder={isVanForm ? 'Transit' : 'Model'}
                  />
                </div>
              </div>

              {isVanForm && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Last MOT Date</label>
                      <input
                        type="date"
                        value={equipmentForm.last_mot_date}
                        onChange={event => updateForm('last_mot_date', event.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Next MOT Due Date</label>
                      <input value={calculateNextMotDueDate(equipmentForm.last_mot_date)} disabled />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Service Interval Miles</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={equipmentForm.service_interval_miles}
                        onChange={event => updateForm('service_interval_miles', event.target.value)}
                        placeholder="10000"
                      />
                    </div>
                    <div className="form-group">
                      <label>Current Odometer Reading</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={equipmentForm.current_odometer_reading}
                        onChange={event => updateForm('current_odometer_reading', event.target.value)}
                        placeholder="54000"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Inflow warehouse #</label>
                    <input
                      value={equipmentForm.inflow_warehouse_code}
                      onChange={event => updateForm('inflow_warehouse_code', event.target.value)}
                      maxLength={60}
                      placeholder="e.g. VAN-01 - links this van's stock to Inflow"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={equipmentForm.is_hired}
                    onChange={event => updateForm('is_hired', event.target.checked)}
                  />
                  Hired equipment
                </label>
              </div>

              {equipmentForm.is_hired && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Hire Reference</label>
                    <input
                      value={equipmentForm.hire_reference}
                      onChange={event => updateForm('hire_reference', event.target.value)}
                      maxLength={100}
                      placeholder="Hire company reference"
                    />
                  </div>
                  <div className="form-group">
                    <label>Hire Start</label>
                    <input
                      type="date"
                      value={equipmentForm.hire_start_date}
                      onChange={event => updateForm('hire_start_date', event.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Hire End</label>
                    <input
                      type="date"
                      value={equipmentForm.hire_end_date}
                      onChange={event => updateForm('hire_end_date', event.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={equipmentForm.notes}
                  onChange={event => updateForm('notes', event.target.value)}
                  rows={3}
                  placeholder="Equipment-specific notes"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={equipmentForm.active}
                    onChange={event => updateForm('active', event.target.checked)}
                  />
                  Equipment is active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Equipment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ComponentTypeSettings />

    </div>
  );
}

export default EquipmentSettings;
